/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes } from 'preact/compat';
import '@/pdp/components/PriceTiers/PriceTiers.css';
import { FunctionalComponent } from 'preact';
import { Price } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Tier } from '@/pdp/data/models';

export interface PriceTiersProps extends HTMLAttributes<HTMLDivElement> {
  locale?: string;
  tiers: Tier[];
  regularPrice?: number; // Base price to calculate savings percentage
}

export const PriceTiers: FunctionalComponent<PriceTiersProps> = ({
  locale,
  tiers,
  regularPrice,
  ...props
}) => {
  const TierPricingLabel = useText('PDP.Product.TierPricing.label').label;

  if (!tiers || tiers.length === 0) {
    return null;
  }

  return (
    <div className="pdp-price-tiers" {...props}>
      {tiers.map((tier, index) => {
        const tierPrice = tier?.tier?.amount ?? 0;
        const quantity = tier?.quantity ?? 0;
        const currency = tier?.tier?.currency ?? '';

        let savingsPercent = 0;
        if (regularPrice && tierPrice && tierPrice < regularPrice) {
          savingsPercent = Math.round(
            ((regularPrice - tierPrice) / regularPrice) * 100
          );
        }

        const textBeforePrice = TierPricingLabel.replace(
          '{quantity}',
          quantity.toString()
        ).split('{price}')[0];

        const textAfterPrice = TierPricingLabel.replace(
          '{quantity}',
          quantity.toString()
        )
          .split('{price}')[1]
          .replace(
            '{percent}',
            savingsPercent > 0 ? savingsPercent.toString() : '0'
          );

        return (
          <div key={index} className="pdp-price-tiers__tier">
            <span className="pdp-price-tiers__text">
              {textBeforePrice}
              <Price
                amount={tierPrice}
                currency={currency}
                locale={locale}
                className="pdp-price-tiers__price"
              />
              {textAfterPrice}
            </span>
          </div>
        );
      })}
    </div>
  );
};
